<script setup lang="ts">
import type { CmsElementCategoryGrid } from "~/composables/useCustom";
import { useBreakpoint } from '@/composables/useBreakpoint';
import { ref } from 'vue';
import SharedImage from "~/components/shared/SharedImage.vue";
import { buildWebpThumbnailUrl } from "~/utils/imageThumbnail";
import { useUserAgent } from "~/composables/useUserAgent";

const props = defineProps<{ content: CmsElementCategoryGrid }>();

const { topText, headlineText, categories } = props.content.data;

const { isMobile, isTablet, isDesktop } = useBreakpoint();

// Set isMounted to true by default to apply animations immediately
const isMounted = ref(true);

const getImageData = (item: any) => {
  if (!item) return {};
  if (isDesktop.value) return item.desktop?.media || {};
  if (isTablet.value) return item.tablet?.media || {};
  if (isMobile.value) return item.mobile?.media || {};
  return {};
};

const getStaggeredStyle = (index: number) => {
  return {
    '--index': index,
  };
};


const bannerData = ref<any>(null);

const category = ref<any>();
try {
  // useCategory may throw if not in a category context
  category.value = useCategory();
} catch (error) {
  console.error('Failed to get category:', error);
}


const topTextData = computed(() =>
  bannerData.value?.slotConfig?.topText?.value ?? topText
);

const headlineTextData = computed(() =>
  bannerData.value?.slotConfig?.headlineText?.value ?? headlineText
);

const getCategoryTitle = (index: number) => {
  return (
    bannerData.value?.slotConfig?.[`category${index + 1}Title`]?.value ||
    categories?.[index]?.title
  );
};

const getCategoryLink = (index: number) => {
  return (
    bannerData.value?.slotConfig?.[`category${index + 1}Link`]?.value ||
    categories?.[index]?.link
  );
};
const getCategoryGradientIntensity = (index: number) => {
  const intensity = categories[index].gradingIntensity;
  return intensity !== undefined && intensity !== null ? `from-black/[${intensity * 10}%]` : 'from-black/50';
};

const getCategoryImageUrl = (index: number, type: string, fetchType = 'media') => {
  // type should be 'Desktop', 'Tablet', or 'Mobile'
  const key = `category${index + 1}image${type}`;
  const apiUrl = bannerData.value?.slotConfig?.[key]?.value?.[0]?.media?.url;
  if (apiUrl) return apiUrl;

  // Fallback to CMS data
  if (fetchType == 'url') {
    return categories?.[index]?.bannerImages?.[0]?.[type.toLowerCase()]?.media?.url || '';
  } else {
    return categories?.[index]?.bannerImages?.[0]?.[type.toLowerCase()]?.media;
  }
};

const getCategoryLinkText = (index: number) => {
  // Try API data first
  const apiText = bannerData.value?.slotConfig?.[`category${index + 1}LinkText`]?.value;
  if (apiText) return apiText;

  // Fallback to CMS data
  return categories?.[index]?.link?.text || '';
};

const getCategoryImageData = (index: number) => {
  // Determine type based on breakpoint
  let type = 'Desktop';
  if (isTablet.value) type = 'Tablet';
  if (isMobile.value) type = 'Mobile';

  // API data
  const key = `category${index + 1}image${type}`;
  const apiMedia = bannerData.value?.slotConfig?.[key]?.value?.[0]?.media;
  if (apiMedia) {
    // Return the media object directly
    return apiMedia;
  }

  // CMS data
  return categories?.[index]?.bannerImages?.[0]?.[type.toLowerCase()]?.media;
};

const CATEGORY_IMAGE_SIZES = [
  { desktop: "527x680", mobile: "680x382" },
  { desktop: "527x680", mobile: "680x382" },
  { desktop: "527x680", mobile: "680x583" },
  { desktop: "663x396", mobile: "680x382" },
  { desktop: "322x264", mobile: "680x382" },
  { desktop: "322x264", mobile: "680x382" },
] as const;

const MOBILE_PRELOAD_INDICES = [2, 1, 0] as const;

const { parsed: userAgent } = useUserAgent();

useHead(() => {
  const link: Array<Record<string, string>> = [];

  if (userAgent.value.isMobile) {
    for (const i of MOBILE_PRELOAD_INDICES) {
      const href = buildWebpThumbnailUrl(
        getCategoryImageUrl(i, "Mobile", "url") as string,
        CATEGORY_IMAGE_SIZES[i].mobile,
      );
      if (!href) continue;

      link.push({
        key: `category-grid-mobile-${i}`,
        rel: "preload",
        as: "image",
        href,
        fetchpriority: i === 2 ? "high" : "auto",
      });
    }
  } else {
    for (let i = 0; i < 6; i++) {
      const href = buildWebpThumbnailUrl(
        getCategoryImageUrl(i, "Desktop", "url") as string,
        CATEGORY_IMAGE_SIZES[i].desktop,
      );
      if (!href) continue;

      link.push({
        key: `category-grid-desktop-${i}`,
        rel: "preload",
        as: "image",
        href,
        fetchpriority: i === 2 ? "high" : "auto",
      });
    }
  }

  return { link };
});

</script>

<template>
  <div class="category-banner pt-8 pb-16 !m-0" role="region" :aria-label="$t('ariaLabelProductCategory')">
    <div class="banner-inner">
      <div class="title" v-if="topTextData || headlineTextData">
        <span class="sub-heading text-base 3xl:text-md font-marcellus text-purple" v-if="topTextData">{{ topTextData }}</span>
        <h1 class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none" v-if="headlineTextData">{{ headlineTextData }}</h1>
      </div>
      <div class="content mt-7 lg:mt-10">
        <!-- Grid container with consistent gap -->
        <div class="flex flex-col md:grid md:grid-cols-12 gap-4 md:gap-5 h-auto lg:h-[420px] xl:h-[480px] 2xl:h-[560px] 3xl:h-[680px]">
          <!-- Left column -->
          <div class="col-span-3 order-2 md:order-1 flex flex-col gap-4 md:gap-5">
            <!-- Configurator card - takes up 1/2 of column height -->
            <SharedButton
              :button="getCategoryLink(0)"
              :isInnerHtml="true"
              :class="['category-card order-2 md:order-1 block relative rounded-[15px] md:rounded-[20px] basis-1/2 overflow-hidden group aspect-[16/9] md:aspect-auto bg-gradient-to-r from-gray-100 to-gray-200 fade-in-up staggered-item', { 'is-mounted': isMounted }]"
              :style="getStaggeredStyle(0)"
              tabindex="0"
              >
              <div class="absolute inset-0">
                <SharedImage
                   v-if="getCategoryImageUrl(0, 'Desktop', 'url') || getCategoryImageUrl(0, 'Tablet', 'url') || getCategoryImageUrl(0, 'Mobile', 'url')"
                  :src="getCategoryImageUrl(0, 'Desktop', 'url')"
                  :sizes="{ desktop: '527x680',tablet: '215x200', mobile: '680x382' }"
                  :alt="getCategoryImageData(0)?.alt || $t('mysofbed')" 
                  :width="getCategoryImageData(0)?.metaData?.width || ''" 
                  :height="getCategoryImageData(0)?.metaData?.height || ''" 
                  loading="eager"
                  :fetchpriority="!isMobile ? 'high' : 'auto'"
                  imgClass="rounded-lg object-cover w-full h-full"
                  :sources="{
                    desktop: getCategoryImageUrl(0, 'Desktop', 'media'),
                    tablet: getCategoryImageUrl(0, 'Tablet'),
                    mobile: getCategoryImageUrl(0, 'Mobile')
                  }"
                />
              </div>
              <div class="relative z-10 h-full flex flex-col justify-end p-6 2xl:p-8 text-white" 
              :class="`${getCategoryGradientIntensity(0)}`"
              v-if="getCategoryTitle(0) || getCategoryLinkText(0)">
                  <h2 class="font-medium mb-1 h6 3xl:h5" v-if="getCategoryTitle(0)">{{ getCategoryTitle(0) }}</h2>
                  <div class="flex items-center gap-2.5" v-if="getCategoryLinkText(0)">
                      <span class="text-base 3xl:text-md font-normal leading-none">{{ getCategoryLinkText(0) }}</span>
                      <i class="icon-arrow-right relative top-[2px] text-xs font-bold"></i>
                  </div>
              </div>
            </SharedButton>
            <!-- Schlafsessel card - takes up 1/2 of column height -->
            <SharedButton
              :button="getCategoryLink(1)"
              :isInnerHtml="true"
              :class="['category-card order-1 md:order-2 block relative rounded-[15px] md:rounded-[20px] basis-1/2 overflow-hidden group aspect-[16/9] md:aspect-auto bg-gradient-to-r from-gray-100 to-gray-200 fade-in-up staggered-item', { 'is-mounted': isMounted }]"
              :style="getStaggeredStyle(1)"
              tabindex="0">
              <div class="absolute inset-0">
                <SharedImage
                  v-if="getCategoryImageUrl(1, 'Desktop', 'url') || getCategoryImageUrl(1, 'Tablet', 'url') || getCategoryImageUrl(1, 'Mobile', 'url')"
                  :src="getCategoryImageUrl(1, 'Desktop', 'url')"
                  :sizes="{ desktop: '527x680',tablet: '215x200', mobile: '680x382' }"
                  :alt="getCategoryImageData(1)?.alt || $t('mysofbed')" 
                  :width="getCategoryImageData(1)?.metaData?.width || ''" 
                  :height="getCategoryImageData(1)?.metaData?.height || ''" 
                  loading="eager"
                  fetchpriority="auto"
                  imgClass="rounded-lg object-cover w-full h-full"
                  :sources="{
                    desktop:  getCategoryImageUrl(1, 'Desktop'),
                    tablet:  getCategoryImageUrl(1, 'Tablet'),
                    mobile:  getCategoryImageUrl(1, 'Mobile')
                  }"
                />
              </div>
              <div class="relative z-10 h-full flex flex-col justify-end p-6 2xl:p-8 text-white bg-gradient-to-t to-transparent" 
              :class="`${getCategoryGradientIntensity(1)}`"
              v-if="getCategoryTitle(1) || getCategoryLinkText(1)">
                  <h2 class="font-medium mb-1 h6 3xl:h5" v-if="getCategoryTitle(1)">{{ getCategoryTitle(1) }}</h2>
                  <div class="flex items-center gap-2.5" v-if="getCategoryLinkText(1)">
                      <span class="text-base 3xl:text-md font-normal leading-none">{{ getCategoryLinkText(1) }}</span>
                      <i class="icon-arrow-right relative top-[2px] text-xs font-bold"></i>
                  </div>
              </div>
            </SharedButton>
          </div>

          <!-- Middle column - full height -->
          <div class="col-span-4 order-1 md:order-2">
            <SharedButton
              :button="getCategoryLink(2)"
              :isInnerHtml="true"
              :class="['category-card block relative rounded-[15px] md:rounded-[20px] h-full overflow-hidden group aspect-[7/6] md:aspect-[1/2] max-w-[100%] lg:aspect-auto bg-gradient-to-r from-gray-100 to-gray-200 fade-in-up staggered-item', { 'is-mounted': isMounted }]"
              :style="getStaggeredStyle(2)"
              tabindex="0">
              <div class="absolute inset-0">
                <SharedImage
                   v-if="getCategoryImageUrl(2, 'Desktop', 'url') || getCategoryImageUrl(2, 'Tablet', 'url') || getCategoryImageUrl(2, 'Mobile', 'url')"
                  :src="getCategoryImageUrl(2, 'Desktop', 'url')"
                  :sizes="{ desktop: '527x680',tablet: '293x420', mobile: '680x583' }"
                 :alt="getCategoryImageData(2)?.alt || $t('mysofbed')" 
                  :width="getCategoryImageData(2)?.metaData?.width || ''" 
                  :height="getCategoryImageData(2)?.metaData?.height || ''" 
                  loading="eager"
                  fetchpriority="high"
                  imgClass="rounded-lg object-cover w-full h-full"
                  :sources="{
                    desktop: getCategoryImageUrl(2, 'Desktop'),
                    tablet: getCategoryImageUrl(2, 'Tablet'),
                    mobile: getCategoryImageUrl(2, 'Mobile')
                  }"
                />
              </div>
              <div class="relative z-10 h-full flex flex-col justify-end p-6 2xl:p-8 text-white bg-gradient-to-t to-transparent" 
              :class="`${getCategoryGradientIntensity(2)}`"
              v-if="getCategoryTitle(2) || getCategoryLinkText(2)">
                  <h2 class="font-medium mb-1 h6 3xl:h5" v-if="getCategoryTitle(2)">{{ getCategoryTitle(2) }}</h2>
                  <div class="flex items-center gap-2.5" v-if="getCategoryLinkText(2)">
                      <span class="text-base 3xl:text-md font-normal leading-none">{{ getCategoryLinkText(2) }}</span>
                      <i class="icon-arrow-right relative top-[2px] text-xs font-bold"></i>
                  </div>
              </div>
            </SharedButton>
          </div>

          <!-- Right column -->
          <div class="col-span-5 order-3 md:order-3 flex flex-col gap-4 md:gap-5">
            <!-- Top card - Bettsofa - takes up 3/5 of column height -->
            <SharedButton
              :button="getCategoryLink(3)"
              :isInnerHtml="true"
              :class="['category-card block relative rounded-[15px] md:rounded-[20px] basis-3/5 overflow-hidden group aspect-[16/9] md:aspect-auto bg-gradient-to-r from-gray-100 to-gray-200 fade-in-up staggered-item', { 'is-mounted': isMounted }]"
              :style="getStaggeredStyle(3)"
              tabindex="0">
              <div class="absolute inset-0">
                <SharedImage
                   v-if="getCategoryImageUrl(3, 'Desktop', 'url') || getCategoryImageUrl(3, 'Tablet', 'url') || getCategoryImageUrl(3, 'Mobile', 'url')"
                  :src="getCategoryImageUrl(3, 'Desktop', 'url')"
                  :sizes="{ desktop: '663x396',tablet: '372x240', mobile: '680x382' }"
                  :alt="getCategoryImageData(3)?.alt || $t('mysofbed')" 
                  :width="getCategoryImageData(3)?.metaData?.width || ''" 
                  :height="getCategoryImageData(3)?.metaData?.height || ''" 
                  :loading="!isMobile ? 'eager' : 'lazy'"
                  :fetchpriority="!isMobile ? 'high' : 'auto'"
                  imgClass="rounded-lg object-cover w-full h-full"
                  :sources="{
                    desktop: getCategoryImageUrl(3, 'Desktop'),
                    tablet: getCategoryImageUrl(3, 'Tablet'),
                    mobile: getCategoryImageUrl(3, 'Mobile')
                  }"
                />
              </div>
              <div class="relative z-10 h-full flex flex-col justify-end p-6 2xl:p-8 text-white bg-gradient-to-t to-transparent" 
              :class="`${getCategoryGradientIntensity(3)}`"
              v-if="getCategoryTitle(3) || getCategoryLinkText(3)">
                  <h2 class="font-medium mb-1 h6 3xl:h5" v-if="getCategoryTitle(3)">{{ getCategoryTitle(3) }}</h2>
                  <div class="flex items-center gap-2.5" v-if="getCategoryLinkText(3)">
                      <span class="text-base 3xl:text-md font-normal leading-none">{{ getCategoryLinkText(3) }}</span>
                      <i class="icon-arrow-right relative top-[2px] text-xs font-bold"></i>
                  </div>
              </div>
            </SharedButton>
              <!-- Bottom grid - takes up 2/5 of column height -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 basis-2/5">
                <!-- Eckschlafsofa -->
              <SharedButton
                :button="getCategoryLink(4)"
                :isInnerHtml="true"
                :class="['category-card block relative rounded-[15px] md:rounded-[20px] h-full overflow-hidden group aspect-[16/9] md:aspect-auto bg-gradient-to-r from-gray-100 to-gray-200 fade-in-up staggered-item', { 'is-mounted': isMounted }]"
                :style="getStaggeredStyle(4)"
                tabindex="0">
                <div class="absolute inset-0">
                  <SharedImage
                     v-if="getCategoryImageUrl(4, 'Desktop', 'url') || getCategoryImageUrl(4, 'Tablet', 'url') || getCategoryImageUrl(4, 'Mobile', 'url')"
                    :src="getCategoryImageUrl(4, 'Desktop', 'url')"
                    :sizes="{ desktop: '322x264',tablet: '176x160', mobile: '680x382' }"
                    :alt="getCategoryImageData(4)?.alt || $t('mysofbed')" 
                    :width="getCategoryImageData(4)?.metaData?.width || ''" 
                    :height="getCategoryImageData(4)?.metaData?.height || ''" 
                    :loading="!isMobile ? 'eager' : 'lazy'"
                    :fetchpriority="!isMobile ? 'high' : 'auto'"
                    imgClass="rounded-lg object-cover w-full h-full"
                    :sources="{
                      desktop: getCategoryImageUrl(4, 'Desktop'),
                      tablet: getCategoryImageUrl(4, 'Tablet'),
                      mobile: getCategoryImageUrl(4, 'Mobile')
                    }"
                  />
                </div>
                <div class="relative z-10 h-full flex flex-col justify-end p-6 2xl:p-8 text-white bg-gradient-to-t to-transparent" 
                :class="`${getCategoryGradientIntensity(4)}`"
                v-if="getCategoryTitle(4) || getCategoryLinkText(4)">
                    <h2 class="font-medium mb-1 h6 3xl:h5" v-if="getCategoryTitle(4)">{{ getCategoryTitle(4) }}</h2>
                    <div class="flex items-center gap-2.5" v-if="getCategoryLinkText(4)">
                        <span class="text-base 3xl:text-md font-normal leading-none">{{ getCategoryLinkText(4) }}</span>
                        <i class="icon-arrow-right relative top-[2px] text-xs font-bold"></i>
                    </div>
                </div>
              </SharedButton>
                <!-- Eckschlafsofa 2 -->
              <SharedButton
                :button="getCategoryLink(5)"
                :isInnerHtml="true"
                :class="['category-card block relative rounded-[15px] md:rounded-[20px] h-full overflow-hidden group aspect-[16/9] md:aspect-auto bg-gradient-to-r from-gray-100 to-gray-200 fade-in-up staggered-item', { 'is-mounted': isMounted }]"
                :style="getStaggeredStyle(5)"
                tabindex="0">
                  <div class="absolute inset-0">  
                    <SharedImage
                      v-if="getCategoryImageUrl(5, 'Desktop', 'url') || getCategoryImageUrl(5, 'Tablet', 'url') || getCategoryImageUrl(5, 'Mobile', 'url')"
                      :src="getCategoryImageUrl(5, 'Desktop', 'url')"
                      :sizes="{ desktop: '322x264',tablet: '176x160', mobile: '680x382' }"
                      :alt="getCategoryImageData(5)?.alt || $t('mysofbed')" 
                      :width="getCategoryImageData(5)?.metaData?.width || ''" 
                      :height="getCategoryImageData(5)?.metaData?.height || ''" 
                      :loading="!isMobile ? 'eager' : 'lazy'"
                      :fetchpriority="!isMobile ? 'high' : 'auto'"
                      imgClass="rounded-lg object-cover w-full h-full"
                      :sources="{
                        desktop: getCategoryImageUrl(5, 'Desktop'),
                        tablet: getCategoryImageUrl(5, 'Tablet'),
                        mobile: getCategoryImageUrl(5, 'Mobile')
                      }"
                    />
                  </div>
                  <div class="relative z-10 h-full flex flex-col justify-end p-6 2xl:p-8 text-white bg-gradient-to-t to-transparent" 
                  :class="`${getCategoryGradientIntensity(5)}`"
                  v-if="getCategoryTitle(5) || getCategoryLinkText(5)">
                      <h2 class="font-medium mb-1 h6 3xl:h5" v-if="getCategoryTitle(5)">{{ getCategoryTitle(5) }}</h2>
                      <div class="flex items-center gap-2.5" v-if="getCategoryLinkText(5)">
                          <span class="text-base 3xl:text-md font-normal leading-none">{{ getCategoryLinkText(5) }}</span>
                          <i class="icon-arrow-right relative top-[2px] text-xs font-bold"></i>
                      </div>
                  </div>
                </SharedButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-in-up {
  opacity: 0;
  transform: translateY(20px);
}

.fade-in-up.is-mounted {
  animation: fadeInUp 0.6s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.staggered-item.is-mounted {
  animation-delay: calc(var(--index) * 50ms);
}

/* Add hover effect for the cards */
.category-card {
  transition: transform 0.1s ease;
}

.category-card:hover {
  transform: translateY(-5px);
}
</style>
